import type React from "react";
import { useEffect, useState } from "react";
import StepProgressBar from "../../progress-bar";
import AddressAutocomplete from "../../misc/AddressAutocomplete";
import CustomSelect from "../../misc/CustomSelect";
import { Button } from "../../misc/Button";
import { STATE_CODE_TO_NAME, US_STATES } from "../../../utils/states";
import InputField from "../../misc/InputField";
import type { SelectedAddress } from "../../../types/selectedAddress";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CustomToast from "../../misc/CustomToast";
import { useNavigate } from "react-router-dom";
import { getAuthHeader } from "../../../utils/authHeader";
import axios from "axios";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { Label } from "../../misc/Label";
import useAppSelector from "../../../hooks/useAppSelector";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../slideshow/Tooltip";
import { Info } from "lucide-react";
import {
    MLS_LISTING_TYPE_INFO,
    MLS_LISTING_TYPE_INFO_TWO,
    MLS_LISTING_TYPE_NOTE,
} from "../../../utils/mlsListingType";

interface CreateUserListingProps {
    setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
    selectedAddress: SelectedAddress | null;
    setSelectedAddress: React.Dispatch<
        React.SetStateAction<SelectedAddress | null>
    >;
    mlsApproved: boolean | null;
    mlsId: string;
    setMlsId: React.Dispatch<React.SetStateAction<string>>;
    mlsWelcome: boolean;
    setListing: React.Dispatch<React.SetStateAction<any>>;
    saleType: "sale" | "rent";
    setSaleType: React.Dispatch<React.SetStateAction<"sale" | "rent">>;
    sharedListing?: boolean;
    setSharedListing?: React.Dispatch<React.SetStateAction<boolean>>;
}

const CreateUserListing: React.FC<CreateUserListingProps> = ({
    setCurrentStep,
    selectedAddress,
    setSelectedAddress,
    mlsApproved,
    mlsId,
    setMlsId,
    mlsWelcome,
    setListing,
    saleType,
    setSaleType,
    sharedListing,
    setSharedListing,
}) => {
    const navigate = useNavigate();

    const handleBack = () => {
        navigate(-1);
    };

    const [userStateUpdated, setUserStateUpdated] = useState(true);
    const [listingTypeInfoOpen, setListingTypeInfoOpen] = useState(false);

    // Read directly from Redux to ensure impersonation state is current
    const reduxMlsApproved = useAppSelector((state) => state.auth.mlsApproved);
    const reduxMlsWelcome = useAppSelector((state) => state.auth.user?.mlsWelcome || false);

    useEffect(() => {
        const checkUserState = async () => {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const headers = await getAuthHeader();

            const response = await axios.post(
                `${backendUrl}/listing/check-user`,
                {
                    mlsApproved: reduxMlsApproved,
                    mlsWelcome: reduxMlsWelcome,
                },
                { headers }
            );

            if (response.status === 200) {
                setUserStateUpdated(response.data);
            }
        };

        checkUserState();
    }, [reduxMlsApproved, reduxMlsWelcome]);

    useEffect(() => {
        if (!userStateUpdated) {
            navigate("/listings", { replace: true });
        }
    }, [userStateUpdated, navigate]);

    const [progressSteps] = useState([
        { label: "Property Address", isActive: true, isCompleted: false },
        {
            label: "Confirm Property Details",
            isActive: false,
            isCompleted: false,
        },
    ]);

    const handleAddressSelect = (address: SelectedAddress) => {
        setSelectedAddress(address);
        setListing(null);
    };

    const handleSaveAndNext = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        // Check if sale type is selected
        if (!saleType) {
            toast.error(
                <CustomToast
                    message="Please select whether this property is for sale or rent."
                    type="error"
                />,
                {
                    autoClose: 3000,
                }
            );
            return;
        }

        if (mlsApproved) {
            if (!mlsId && !selectedAddress) {
                toast.error(
                    <CustomToast
                        message="Please enter MLS Number or select an address before proceeding."
                        type="error"
                    />,
                    { autoClose: 3000 }
                );
                return;
            }

            if (mlsId && selectedAddress) {
                toast.error(
                    <CustomToast
                        message="Please enter either MLS Number or select an address, not both."
                        type="error"
                    />,
                    { autoClose: 3000 }
                );
                return;
            }
        } else {
            if (!selectedAddress) {
                toast.error(
                    <CustomToast
                        message="Please select an address before proceeding."
                        type="error"
                    />,
                    {
                        autoClose: 3000,
                    }
                );
                return;
            }
        }
        setCurrentStep(2);
    };

    const handleReset = () => {
        setListing(null);
        setSelectedAddress(null);
        setMlsId("");
        setSaleType("sale"); // Reset to default sale type
        setSharedListing?.(false);
    };

    const isMobile = useIsMobile(900);

    const handleMlsIdChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const newValue = e.target.value;
        // Allow only digits (or an empty string)
        if (/^\d*$/.test(newValue)) {
            // If MLS id changes, clear any previously loaded listing/details
            if (newValue !== mlsId) {
                setListing(null);
                // Also clear selected address to avoid showing stale address
                setSelectedAddress(null);
                // consider it an entirely new workflow and retrigger all of the same checks as part of the process.
                setSharedListing?.(false);
            }
            setMlsId(newValue);
        }
    };

    return (
        <main
            className={`flex min-h-full overflow-auto flex-col justify-center items-center ${
                isMobile ? "bg-white" : "bg-background"
            } shadow-[0px_4px_4px_rgba(0,0,0,0.25)] max-md:px-5`}
        >
            <section
                className={`flex flex-col items-center ${
                    isMobile ? "p-4 h-[84vh]" : "px-8 py-12"
                } bg-white w-full  max-w-3xl max-md:px-4 max-md:py-8 relative`}
            >
                <header className="flex flex-col mx-auto w-full">
                    <h1 className="my-2 text-primary text-2xl font-bold leading-none text-center">
                        Create Listing
                    </h1>
                    <StepProgressBar
                        steps={progressSteps.filter((step) => step)}
                    />
                </header>
                <form
                    className={`flex flex-col  flex-1 mt-2 w-full max-w-full`}
                    onSubmit={handleSaveAndNext}
                >
                    <div className={`flex-1 flex flex-col justify-center`}>
                        {/* Sale Type Selection */}
                        <div className="flex flex-col my-3">
                            <Label className="text-primary my-2">
                                Listing Ownership
                            </Label>
                            <div className="flex gap-8">
                                <button
                                    type="button"
                                    onClick={() => setSharedListing?.(false)}
                                    className={`flex-1 py-1.5 px-4 rounded-md border ${
                                        !sharedListing
                                            ? "bg-secondary text-white border-secondary"
                                            : "bg-white text-primary border-gray-300 hover:border-secondary"
                                    }`}
                                >
                                    My Listing
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setSharedListing?.(true)}
                                    className={`flex-1 py-1.5 px-4 rounded-md border ${
                                        sharedListing
                                            ? "bg-secondary text-white border-secondary"
                                            : "bg-white text-primary border-gray-300 hover:border-secondary"
                                    }`}
                                >
                                    Listing by Another Agent
                                </button>
                            </div>
                        </div>
                        {mlsApproved ? (
                            <div className="flex flex-col my-3">
                                <div className="flex items-center gap-1 my-2">
                                    <Label className="text-primary">
                                        Listing Type
                                    </Label>
                                    <TooltipProvider>
                                        <Tooltip
                                            open={listingTypeInfoOpen}
                                            onOpenChange={setListingTypeInfoOpen}
                                        >
                                            <TooltipTrigger asChild>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-6 w-6"
                                                    onClick={(e) => {
                                                        // Radix closes the tooltip on trigger click; keep it
                                                        // open so a tap works on mobile.
                                                        e.preventDefault();
                                                        setListingTypeInfoOpen(true);
                                                    }}
                                                >
                                                    <Info className="h-4 w-4" />
                                                    <span className="sr-only">
                                                        Where the listing type comes from
                                                    </span>
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent
                                                side="bottom"
                                                align="start"
                                                collisionPadding={16}
                                                className="max-w-[calc(100vw-2rem)] min-[480px]:max-w-md"
                                            >
                                                <div className="text-sm text-white">
                                                    <p className="font-medium mb-2">
                                                        Where this comes from
                                                    </p>
                                                    <p>{MLS_LISTING_TYPE_INFO}</p>
                                                    <p className="mt-2">
                                                        {MLS_LISTING_TYPE_INFO_TWO}
                                                    </p>
                                                </div>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>
                                <div className="rounded-md border border-secondary bg-secondary/10 p-3 text-left text-sm text-primary">
                                    {MLS_LISTING_TYPE_NOTE}
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col my-3">
                                <Label className="text-primary my-2">
                                    Listing Type
                                </Label>
                                <div className="flex gap-8">
                                    <button
                                        type="button"
                                        onClick={() => setSaleType("sale")}
                                        className={`flex-1 py-1.5 px-4 rounded-md border ${
                                            saleType === "sale"
                                                ? "bg-secondary text-white border-secondary"
                                                : "bg-white text-primary border-gray-300 hover:border-secondary"
                                        }`}
                                    >
                                        For Sale
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setSaleType("rent")}
                                        className={`flex-1 py-1.5 px-4 rounded-md border ${
                                            saleType === "rent"
                                                ? "bg-secondary text-white border-secondary"
                                                : "bg-white text-primary border-gray-300 hover:border-secondary"
                                        }`}
                                    >
                                        For Rent
                                    </button>
                                </div>
                            </div>
                        )}
                        {/* Address Autocomplete Component */}
                        {mlsApproved && (
                            <div className="flex flex-col justify-between">
                                <InputField
                                    label="MLS Number"
                                    placeholder="Enter MLS Number"
                                    type="text"
                                    className="mt-2"
                                    value={mlsId}
                                    onChange={(e) => handleMlsIdChange(e)}
                                />

                                <span className="mt-8 text-center text-secondary">
                                    {" "}
                                    OR{" "}
                                </span>
                            </div>
                        )}

                        {!selectedAddress ? (
                            <AddressAutocomplete
                                onAddressSelect={handleAddressSelect}
                                label={"Enter Property Address"}
                                className="top-24 mt-2"
                            />
                        ) : (
                            <div className="flex-1 flex flex-col justify-center">
                                <h2 className="text-lg font-semibold text-primary mb-4">
                                    Enter Property Address
                                </h2>
                                <div className="space-y-4">
                                    <AddressAutocomplete
                                        label="Address 1"
                                        placeholder="Ex: 5000 Stonewood Dr"
                                        value={selectedAddress.address1}
                                        onInputChange={(value) => {
                                            setListing(null);
                                            setSelectedAddress({
                                                ...selectedAddress,
                                                address1: value,
                                            });
                                        }}
                                        onAddressSelect={(address) => {
                                            setSelectedAddress(address);
                                        }}
                                        className="relative"
                                    />
                                    <InputField
                                        label="Address 2"
                                        placeholder="Apartment, suite, unit, etc."
                                        type="text"
                                        value={selectedAddress.address2 || ""}
                                        onChange={(e) =>
                                            setSelectedAddress({
                                                ...selectedAddress,
                                                address2: e.target.value,
                                            })
                                        }
                                    />
                                    <div className="flex flex-col gap-2">
                                        <div className="flex flex-col md:flex-row gap-4 w-full md:items-end">
                                            <InputField
                                                label="City"
                                                placeholder="Ex: Wexford"
                                                type="text"
                                                value={selectedAddress.city}
                                                onChange={(e) =>
                                                    setSelectedAddress({
                                                        ...selectedAddress,
                                                        city: e.target.value,
                                                    })
                                                }
                                                className="w-full md:flex-1"
                                            />
                                            <CustomSelect
                                                id="state-select-dropdown"
                                                label="State"
                                                options={US_STATES.map(
                                                    (state) => ({
                                                        label: `${state.label} - ${state.value}`,
                                                        value: state.value,
                                                    })
                                                )}
                                                value={
                                                    selectedAddress.state
                                                        ? {
                                                              label: `${
                                                                  STATE_CODE_TO_NAME[
                                                                      selectedAddress
                                                                          .state
                                                                  ]
                                                              } - ${
                                                                  selectedAddress.state
                                                              }`,
                                                              value: selectedAddress.state,
                                                          }
                                                        : null
                                                }
                                                onChange={(selectedOption) =>
                                                    setSelectedAddress(
                                                        (prevDetails) => ({
                                                            ...prevDetails!,
                                                            state: selectedOption
                                                                ? selectedOption.value
                                                                : "",
                                                        })
                                                    )
                                                }
                                                placeholder="Select State"
                                                className="w-full md:flex-1 min-h-[60px] text-primary"
                                            />
                                        </div>
                                        <div className="flex flex-col md:flex-row mt-4 w-full">
                                            <InputField
                                                label="Zip Code"
                                                placeholder="Ex: 15090"
                                                type="text"
                                                value={selectedAddress.zipCode}
                                                onChange={(e) =>
                                                    setSelectedAddress({
                                                        ...selectedAddress,
                                                        zipCode: e.target.value,
                                                    })
                                                }
                                                className="w-full md:flex-1"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={`w-full mt-7 flex flex-row gap-x-10 ${isMobile ? "my-6" : ''}`}>
                        {mlsId.length > 0 || selectedAddress !== null ? (
                            <Button
                                className="w-full md:w-auto"
                                onClick={handleReset}
                                type="reset"
                            >
                                Reset
                            </Button>
                        ) : (
                            <Button
                                className="w-full md:w-auto"
                                onClick={handleBack}
                                type="button"
                            >
                                Back
                            </Button>
                        )}
                        <Button className="w-full md:w-auto" type="submit">
                            Next
                        </Button>
                    </div>
                </form>
            </section>
        </main>
    );
};

export default CreateUserListing;
